/**
 * GroupsManager
 *
 * Beheer al je groepen (Mastermind / Netwerkclub / Community):
 * aanmaken, bewerken, verwijderen — op één plek, buiten de onboarding.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth, db, storage } from '../../firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import {
  CommunityIcon,
  ThinkIcon,
  PartnerIcon,
  SettingsIcon,
  DeleteIcon,
  EditIcon,
  CloseIcon,
  SuccessIcon,
  WarningIcon,
  AddIcon,
} from '../icons';
import { Icon } from '../icons';
import { Users, ChevronRight, AlertCircle, Loader, Camera, ImageIcon, X as XIcon } from 'lucide-react';
import { compressForCover } from '../../utils/imageCompressor';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EntityType = 'mastermind' | 'networkclub' | 'community';

interface GroupEntity {
  id: string;
  name: string;
  description?: string;
  type: EntityType;
  role: 'owner' | 'organizer' | 'member';
  memberCount?: number;
  isActive?: boolean;
  visibility?: 'public' | 'private';
  kvkNumber?: string;
  logoUrl?: string;
  coverImageUrl?: string;
  coverPosition?: { x: number; y: number };
  createdAt?: Timestamp;
}

interface GroupsManagerProps {
  onClose?: () => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Image upload helper
// ---------------------------------------------------------------------------

async function uploadCoverImage(entityId: string, file: File): Promise<string> {
  // Compress client-side via shared utility (max 1200px, ~82% quality)
  const compressed = await compressForCover(file);
  const path = `groupCovers/${entityId}/${Date.now()}-${compressed.name}`;
  const fileRef = storageRef(storage, path);
  await uploadBytes(fileRef, compressed);
  return getDownloadURL(fileRef);
}

// ---------------------------------------------------------------------------
// CoverImageUpload — reusable upload widget
// ---------------------------------------------------------------------------

interface CoverImageUploadProps {
  /** Current cover URL (if any) */
  value?: string;
  /** Current object-position percentages */
  position?: { x: number; y: number };
  /** Called with new URL after successful upload, or '' when removed */
  onChange: (url: string) => void;
  /** Called when cover position changes */
  onPositionChange?: (position: { x: number; y: number }) => void;
  /** entityId used for storage path; use 'new' during create flow */
  entityId: string;
  /** Type drives the placeholder gradient */
  entityType?: EntityType;
}

function CoverImageUpload({
  value,
  position,
  onChange,
  onPositionChange,
  entityId,
  entityType = 'mastermind',
}: CoverImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [coverPosition, setCoverPosition] = useState<{ x: number; y: number }>(position || { x: 50, y: 50 });

  useEffect(() => {
    if (position && typeof position.x === 'number' && typeof position.y === 'number') {
      setCoverPosition({
        x: Math.max(0, Math.min(100, position.x)),
        y: Math.max(0, Math.min(100, position.y)),
      });
    }
  }, [position?.x, position?.y]);

  const gradients: Record<EntityType, string> = {
    mastermind: 'linear-gradient(135deg, #EDE9F4 0%, #D8D0E8 100%)',
    networkclub: 'linear-gradient(135deg, #E2EFEC 0%, #C4D8D4 100%)',
    community:   'linear-gradient(135deg, #E0EEE2 0%, #C2D5C4 100%)',
  };

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Alleen afbeeldingsbestanden zijn toegestaan.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Maximaal 8 MB per afbeelding.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const url = await uploadCoverImage(entityId, file);
      onChange(url);
    } catch (e) {
      console.error('Cover upload error:', e);
      setError('Upload mislukt. Probeer opnieuw.');
    } finally {
      setUploading(false);
    }
  };

  const updatePositionFromPoint = useCallback((clientX: number, clientY: number) => {
    const frame = frameRef.current;
    if (!frame) return;
    const rect = frame.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    const next = {
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y)),
    };
    setCoverPosition(next);
    onPositionChange?.(next);
  }, [onPositionChange]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => updatePositionFromPoint(e.clientX, e.clientY);
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, updatePositionFromPoint]);

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-[#3C4F49]">
        Omslagafbeelding <span className="text-[#8A9490] font-normal">(optioneel)</span>
      </label>

      {/* Preview / upload zone */}
      <div
        ref={frameRef}
        className="relative w-full rounded-xl overflow-hidden cursor-pointer group"
        style={{ height: 120 }}
        onClick={() => {
          // When an image exists, dragging is primary behavior; use "Wijzigen" to upload another file.
          if (!value && !uploading) inputRef.current?.click();
        }}
        onMouseDown={(e) => {
          if (!value || uploading) return;
          e.preventDefault();
          setIsDragging(true);
          updatePositionFromPoint(e.clientX, e.clientY);
        }}
        title={value ? 'Sleep om foto te verschuiven (horizontaal/verticaal)' : undefined}
      >
        {value ? (
          <>
            <img
              src={value}
              alt="Omslagafbeelding"
              className="w-full h-full object-cover"
              draggable={false}
              style={{ objectPosition: `${coverPosition.x}% ${coverPosition.y}%` }}
            />
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 bg-white/90 rounded-lg px-3 py-1.5 text-xs font-medium text-[#3C4F49]">
                <Camera size={13} />
                Wijzigen
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (!uploading) inputRef.current?.click(); }}
              className="absolute top-2 left-2 px-2.5 py-1 bg-black/50 hover:bg-black/70 rounded-md text-white text-[11px] font-medium transition-colors"
              title="Andere omslag kiezen"
            >
              Wijzigen
            </button>
            {/* Remove button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
                const def = { x: 50, y: 50 };
                setCoverPosition(def);
                onPositionChange?.(def);
              }}
              className="absolute top-2 right-2 w-6 h-6 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors"
              title="Verwijderen"
            >
              <XIcon size={12} color="white" />
            </button>
          </>
        ) : (
          <div
            className="w-full h-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#C4D8D4] rounded-xl hover:border-[#9FB8AE] hover:bg-[#F2F7F5] transition-all"
            style={{ background: gradients[entityType] }}
          >
            {uploading ? (
              <><Loader size={18} className="animate-spin text-[#7A9488]" /><span className="text-xs text-[#7A9488]">Uploaden...</span></>
            ) : (
              <><ImageIcon size={20} className="text-[#9FB8AE]" /><span className="text-xs text-[#7A9488] font-medium">Klik om een omslagfoto te kiezen</span></>
            )}
          </div>
        )}

        {/* Loading overlay while uploading over existing image */}
        {uploading && value && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-xl">
            <Loader size={22} className="animate-spin text-white" />
          </div>
        )}
      </div>

      {error && (
        <p className="text-xs text-[#9B6060] flex items-center gap-1">
          <AlertCircle size={12} />{error}
        </p>
      )}
      {value && (
        <p className="text-xs text-[#8A9490]">Tip: sleep de afbeelding om horizontaal en verticaal te positioneren.</p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}

// Sage palette (matches mintSageTheme.css)
const SAGE = {
  bg: '#F4F6F5',
  bgLight: '#F8FAF9',
  border: '#C8D8D4',
  borderLight: '#DDE8E5',
  text: '#4F6660',
  textLight: '#7A9488',
  textDark: '#3C4F49',
} as const;

const TYPE_CONFIG: Record<EntityType, {
  label: string;
  description: string;
  colorClass: string;      // Tailwind text color
  bgClass: string;         // Tailwind bg
  borderClass: string;     // Tailwind border
  iconBg: string;          // inline style bg
  iconColor: string;       // inline style color
  Icon: React.ComponentType<any>;
}> = {
  mastermind: {
    label: 'Mastermind',
    description: 'Kleine besloten groep (4-8 personen) gericht op groeien via accountability en peer-coaching.',
    colorClass: 'text-[#6B5E82]',
    bgClass: 'bg-[#F5F3F8]',
    borderClass: 'border-[#D8D0E8]',
    iconBg: '#EDE9F4',
    iconColor: '#6B5E82',
    Icon: ThinkIcon,
  },
  networkclub: {
    label: 'Netwerkclub',
    description: 'Gestructureerd netwerk met vaste bijeenkomsten, meetingagenda en KvK-koppeling.',
    colorClass: 'text-[#4F6660]',
    bgClass: 'bg-[#EEF5F2]',
    borderClass: 'border-[#C4D8D4]',
    iconBg: '#E2EFEC',
    iconColor: '#4F6660',
    Icon: PartnerIcon,
  },
  community: {
    label: 'Community',
    description: 'Open of besloten community rond een thema, sector of gedeelde visie.',
    colorClass: 'text-[#5A7060]',
    bgClass: 'bg-[#EFF5EF]',
    borderClass: 'border-[#C2D5C4]',
    iconBg: '#E0EEE2',
    iconColor: '#5A7060',
    Icon: CommunityIcon,
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function loadUserGroups(userId: string): Promise<GroupEntity[]> {
  const membersSnap = await getDocs(
    query(collection(db, 'entityMembers'), where('userId', '==', userId))
  );
  if (membersSnap.empty) return [];

  const results: GroupEntity[] = [];
  await Promise.all(
    membersSnap.docs.map(async (memberDoc) => {
      const memberData = memberDoc.data();
      const entitySnap = await getDoc(doc(db, 'entities', memberData.entityId));
      if (!entitySnap.exists()) return;
      const entity = entitySnap.data() as any;
      if (!['mastermind', 'networkclub', 'community'].includes(entity.type)) return;
      results.push({
        id: entitySnap.id,
        name: entity.name,
        description: entity.description,
        type: entity.type as EntityType,
        role: memberData.role || 'member',
        memberCount: entity.memberCount ?? 1,
        isActive: entity.isActive,
        visibility: entity.visibility,
        kvkNumber: entity.kvkNumber,
        logoUrl: entity.logoUrl,
        coverImageUrl: entity.coverImageUrl,
        coverPosition: entity.coverPosition,
        createdAt: entity.createdAt,
      });
    })
  );

  // Sort: owner first, then by name
  results.sort((a, b) => {
    const roleOrder = { owner: 0, organizer: 1, member: 2 };
    const rDiff = roleOrder[a.role] - roleOrder[b.role];
    if (rDiff !== 0) return rDiff;
    return a.name.localeCompare(b.name);
  });

  return results;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function RoleBadge({ role }: { role: GroupEntity['role'] }) {
  const cfg = {
    owner:      'bg-[#F2EFE6] text-[#6B5A3E] border-[#D8CFBC]',
    organizer:  'bg-[#EEF3F2] text-[#4F6660] border-[#C4D5D0]',
    member:     'bg-[#F4F4F1] text-[#6B7170] border-[#DDDBD5]',
  }[role];
  const label = { owner: 'Organisator', organizer: 'Organisator', member: 'Lid' }[role];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${cfg}`}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Create Form
// ---------------------------------------------------------------------------

interface CreateFormProps {
  onCreated: (entityId: string, type: EntityType) => void;
  onCancel: () => void;
  userId: string;
  userName: string;
  userEmail: string;
}

function CreateForm({ onCreated, onCancel, userId, userName, userEmail }: CreateFormProps) {
  const [step, setStep] = useState<'pick-type' | 'fill-details'>('pick-type');
  const [selectedType, setSelectedType] = useState<EntityType | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kvkNumber, setKvkNumber] = useState('');
  const [visibility, setVisibility] = useState<'private' | 'public'>('private');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [coverPosition, setCoverPosition] = useState<{ x: number; y: number }>({ x: 50, y: 50 });
  const [pendingCoverFile, setPendingCoverFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!selectedType || !name.trim()) {
      setError('Vul een naam in.');
      return;
    }
    if (selectedType === 'networkclub' && kvkNumber && !/^\d{8}$/.test(kvkNumber.trim())) {
      setError('KvK-nummer moet precies 8 cijfers zijn.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // 1. Create entity doc
      const entityRef = await addDoc(collection(db, 'entities'), {
        type: selectedType,
        name: name.trim(),
        description: description.trim() || '',
        ownerId: userId,
        ownerName: userName,
        ownerEmail: userEmail,
        visibility,
        isPublic: visibility === 'public',
        isActive: true,
        isVisible: false,
        memberCount: 1,
        maxMembers: selectedType === 'mastermind' ? 10 : 100,
        requiresSubscription: false,
        ownerHasSubscription: true,
        ...(kvkNumber.trim() ? { kvkNumber: kvkNumber.trim() } : {}),
        // coverImageUrl is set after upload (step 1b below)
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const entityId = entityRef.id;

      // 1b. Upload cover image now that we have entityId
      let finalCoverUrl = coverImageUrl;
      if (pendingCoverFile) {
        try {
          finalCoverUrl = await uploadCoverImage(entityId, pendingCoverFile);
          await updateDoc(doc(db, 'entities', entityId), {
            coverImageUrl: finalCoverUrl,
            coverPosition,
          });
        } catch { /* non-critical — group is created without cover */ }
      } else if (finalCoverUrl) {
        await updateDoc(doc(db, 'entities', entityId), {
          coverImageUrl: finalCoverUrl,
          coverPosition,
        });
      }

      // 2. Add owner as member
      await setDoc(doc(db, 'entityMembers', `${entityId}_${userId}`), {
        entityId,
        userId,
        role: 'owner',
        name: userName,
        email: userEmail,
        createdAt: serverTimestamp(),
        createdBy: userId,
      });

      // 3. Create a networkClubs document for ALL group types (including mastermind)
      // so hubs can always resolve entityId -> club profile immediately.
      if (selectedType === 'networkclub' || selectedType === 'community' || selectedType === 'mastermind') {
        const isMastermind = selectedType === 'mastermind';
        await addDoc(collection(db, 'networkClubs'), {
          name: name.trim(),
          description: description.trim() || '',
          type: selectedType,
          entityId,
          organizerId: userId,
          organizerName: userName,
          organizerEmail: userEmail,
          region: '',
          country: 'Nederland',
          moderationStatus: isMastermind ? 'approved' : 'pending',
          isVerified: isMastermind,
          isActive: isMastermind,
          isVisible: isMastermind,
          memberCount: 1,
          ...(kvkNumber.trim() ? { kvkNumber: kvkNumber.trim() } : {}),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      onCreated(entityId, selectedType!);
    } catch (err) {
      console.error('Error creating group:', err);
      setError('Aanmaken mislukt. Probeer het opnieuw.');
    } finally {
      setSaving(false);
    }
  };

  if (step === 'pick-type') {
    return (
      <div className="space-y-5">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Kies het type groep</h3>
          <p className="text-sm text-gray-500">Elke groep heeft een eigen structuur en doel.</p>
        </div>

        <div className="space-y-3">
          {(Object.entries(TYPE_CONFIG) as [EntityType, typeof TYPE_CONFIG[EntityType]][]).map(([type, cfg]) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-start gap-4 ${
                selectedType === type
                  ? `${cfg.borderClass} ${cfg.bgClass}`
                  : 'border-[#E9E7E2] hover:border-[#C8D8D4] hover:bg-[#F8FAF9]'
              }`}
            >
              <div
                className="mt-0.5 w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: selectedType === type ? cfg.iconBg : '#F0EEE9' }}
              >
                <cfg.Icon
                  size={20}
                  weight="regular"
                  style={{ color: selectedType === type ? cfg.iconColor : '#8A9490' }}
                />
              </div>
              <div>
                <p className={`font-semibold text-sm ${selectedType === type ? cfg.colorClass : 'text-[#3C4F49]'}`}>
                  {cfg.label}
                </p>
                <p className="text-xs text-[#6B7A75] mt-0.5 leading-relaxed">{cfg.description}</p>
              </div>
              {selectedType === type && (
                <SuccessIcon size={18} weight="fill" className={`${cfg.colorClass} ml-auto mt-0.5 shrink-0`} />
              )}
            </button>
          ))}
        </div>

        <div className="flex gap-3 pt-2">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 text-sm text-[#4F6660] border border-[#D8E4E0] rounded-lg hover:bg-[#F4F6F5] transition-colors"
          >
            Annuleren
          </button>
          <button
            onClick={() => selectedType && setStep('fill-details')}
            disabled={!selectedType}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: '#8DA8A0' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#7A9488')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#8DA8A0')}
          >
            Volgende →
          </button>
        </div>
      </div>
    );
  }

  // fill-details step
  const cfg = TYPE_CONFIG[selectedType!];
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button
          onClick={() => setStep('pick-type')}
          className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
        >
          <ChevronRight size={16} className="rotate-180 text-gray-500" />
        </button>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {cfg.label} aanmaken
          </h3>
          <p className="text-xs text-gray-500">Vul de gegevens in</p>
        </div>
      </div>

        <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#3C4F49] mb-1.5">
            Naam <span className="text-[#B56B6B]">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`Naam van de ${cfg.label.toLowerCase()}`}
            className="w-full px-3 py-2.5 border border-[#D8E4E0] rounded-lg text-sm outline-none transition"
            style={{ '--tw-ring-color': '#9FB8AE' } as React.CSSProperties}
            onFocus={e => e.currentTarget.style.borderColor = '#9FB8AE'}
            onBlur={e => e.currentTarget.style.borderColor = '#D8E4E0'}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#3C4F49] mb-1.5">
            Omschrijving
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Beschrijf het doel van deze groep..."
            rows={3}
            className="w-full px-3 py-2.5 border border-[#D8E4E0] rounded-lg text-sm outline-none transition resize-none"
            onFocus={e => e.currentTarget.style.borderColor = '#9FB8AE'}
            onBlur={e => e.currentTarget.style.borderColor = '#D8E4E0'}
          />
        </div>

        {selectedType === 'networkclub' && (
          <div>
            <label className="block text-sm font-medium text-[#3C4F49] mb-1.5">
              KvK-nummer <span className="text-[#8A9490] font-normal">(optioneel)</span>
            </label>
            <input
              type="text"
              value={kvkNumber}
              onChange={(e) => setKvkNumber(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="12345678"
              maxLength={8}
              className="w-full px-3 py-2.5 border border-[#D8E4E0] rounded-lg text-sm outline-none transition font-mono"
              onFocus={e => e.currentTarget.style.borderColor = '#9FB8AE'}
              onBlur={e => e.currentTarget.style.borderColor = '#D8E4E0'}
            />
          </div>
        )}

        {/* Cover image upload */}
        <CoverImageUpload
          value={coverImageUrl}
          onChange={setCoverImageUrl}
          position={coverPosition}
          onPositionChange={setCoverPosition}
          entityId="new"
          entityType={selectedType ?? 'mastermind'}
        />

        <div>
          <label className="block text-sm font-medium text-[#3C4F49] mb-2">
            Zichtbaarheid
          </label>
          <div className="flex gap-3">
            {(['private', 'public'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setVisibility(v)}
                className={`flex-1 px-3 py-2.5 text-sm rounded-lg border-2 transition-all ${
                  visibility === v
                    ? 'border-[#8DA8A0] bg-[#EEF5F2] text-[#4F6660] font-medium'
                    : 'border-[#E0EBE8] text-[#6B7A75] hover:border-[#C4D8D4]'
                }`}
              >
                {v === 'private' ? '🔒 Besloten' : '🌐 Openbaar'}
              </button>
            ))}
          </div>
          <p className="text-xs text-[#8A9490] mt-1.5">
            {visibility === 'private'
              ? 'Alleen uitgenodigde leden kunnen deelnemen.'
              : 'Iedereen kan de groep vinden en aanmelden.'}
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-[#FDF5F5] border border-[#E8CECE] rounded-lg text-sm text-[#8B5050]">
          <AlertCircle size={15} className="shrink-0" />
          {error}
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 text-sm text-[#4F6660] border border-[#D8E4E0] rounded-lg hover:bg-[#F4F6F5] transition-colors"
        >
          Annuleren
        </button>
        <button
          onClick={handleCreate}
          disabled={saving || !name.trim()}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          style={{ backgroundColor: '#8DA8A0' }}
          onMouseEnter={e => { if (!saving && name.trim()) e.currentTarget.style.backgroundColor = '#7A9488'; }}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#8DA8A0')}
        >
          {saving ? (
            <><Loader size={14} className="animate-spin" /> Aanmaken...</>
          ) : (
            'Groep aanmaken'
          )}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit Modal
// ---------------------------------------------------------------------------

interface EditModalProps {
  group: GroupEntity;
  onSaved: () => void;
  onClose: () => void;
}

function EditModal({ group, onSaved, onClose }: EditModalProps) {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description || '');
  const [kvkNumber, setKvkNumber] = useState(group.kvkNumber || '');
  const [visibility, setVisibility] = useState<'private' | 'public'>(group.visibility || 'private');
  const [coverImageUrl, setCoverImageUrl] = useState(group.coverImageUrl || '');
  const [coverPosition, setCoverPosition] = useState<{ x: number; y: number }>(
    group.coverPosition || { x: 50, y: 50 }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Naam is verplicht.');
      return;
    }
    if (group.type === 'networkclub' && kvkNumber && !/^\d{8}$/.test(kvkNumber.trim())) {
      setError('KvK-nummer moet precies 8 cijfers zijn.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updates: Record<string, any> = {
        name: name.trim(),
        description: description.trim(),
        visibility,
        isPublic: visibility === 'public',
        coverImageUrl: coverImageUrl || '',
        coverPosition,
        updatedAt: serverTimestamp(),
      };
      if (group.type === 'networkclub') {
        updates.kvkNumber = kvkNumber.trim();
      }

      await updateDoc(doc(db, 'entities', group.id), updates);

      // Keep networkClubs document in sync as well (all group types when doc exists)
      const ncSnap = await getDocs(
        query(collection(db, 'networkClubs'), where('entityId', '==', group.id))
      );
      if (!ncSnap.empty) {
        await updateDoc(ncSnap.docs[0].ref, {
          name: name.trim(),
          description: description.trim(),
          coverImageUrl: coverImageUrl || '',
          coverPosition,
          ...(group.type === 'networkclub' ? { kvkNumber: kvkNumber.trim() } : {}),
          updatedAt: serverTimestamp(),
        });
      }

      onSaved();
    } catch (err) {
      console.error('Error updating group:', err);
      setError('Opslaan mislukt. Probeer het opnieuw.');
    } finally {
      setSaving(false);
    }
  };

  const cfg = TYPE_CONFIG[group.type];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-5 border border-[#E9E7E2]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: cfg.iconBg }}>
              <cfg.Icon size={18} weight="regular" style={{ color: cfg.iconColor }} />
            </div>
            <h3 className="text-base font-semibold text-[#3C4F49]">{cfg.label} bewerken</h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-[#F4F4F1] flex items-center justify-center transition-colors"
          >
            <CloseIcon size={16} weight="regular" className="text-[#8A9490]" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#3C4F49] mb-1.5">Naam *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2.5 border border-[#D8E4E0] rounded-lg text-sm outline-none transition"
              onFocus={e => e.currentTarget.style.borderColor = '#9FB8AE'}
              onBlur={e => e.currentTarget.style.borderColor = '#D8E4E0'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#3C4F49] mb-1.5">Omschrijving</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 border border-[#D8E4E0] rounded-lg text-sm outline-none transition resize-none"
              onFocus={e => e.currentTarget.style.borderColor = '#9FB8AE'}
              onBlur={e => e.currentTarget.style.borderColor = '#D8E4E0'}
            />
          </div>

          {group.type === 'networkclub' && (
            <div>
              <label className="block text-sm font-medium text-[#3C4F49] mb-1.5">
                KvK-nummer <span className="text-[#8A9490] font-normal">(optioneel)</span>
              </label>
              <input
                type="text"
                value={kvkNumber}
                onChange={(e) => setKvkNumber(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="12345678"
                maxLength={8}
                className="w-full px-3 py-2.5 border border-[#D8E4E0] rounded-lg text-sm outline-none transition font-mono"
                onFocus={e => e.currentTarget.style.borderColor = '#9FB8AE'}
                onBlur={e => e.currentTarget.style.borderColor = '#D8E4E0'}
              />
            </div>
          )}

          {/* Cover image */}
          <CoverImageUpload
            value={coverImageUrl}
            onChange={setCoverImageUrl}
            position={coverPosition}
            onPositionChange={setCoverPosition}
            entityId={group.id}
            entityType={group.type}
          />

          <div>
            <label className="block text-sm font-medium text-[#3C4F49] mb-2">Zichtbaarheid</label>
            <div className="flex gap-3">
              {(['private', 'public'] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setVisibility(v)}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                    visibility === v
                      ? 'border-[#8DA8A0] bg-[#EEF5F2] text-[#4F6660] font-medium'
                      : 'border-[#E0EBE8] text-[#6B7A75] hover:border-[#C4D8D4]'
                  }`}
                >
                  {v === 'private' ? '🔒 Besloten' : '🌐 Openbaar'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-[#FDF5F5] border border-[#E8CECE] rounded-lg text-sm text-[#8B5050]">
            <AlertCircle size={15} className="shrink-0" />
            {error}
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm text-[#4F6660] border border-[#D8E4E0] rounded-lg hover:bg-[#F4F6F5] transition-colors"
          >
            Annuleren
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
            style={{ backgroundColor: '#8DA8A0' }}
            onMouseEnter={e => { if (!saving && name.trim()) e.currentTarget.style.backgroundColor = '#7A9488'; }}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#8DA8A0')}
          >
            {saving ? <><Loader size={14} className="animate-spin" /> Opslaan...</> : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delete Confirm
// ---------------------------------------------------------------------------

interface DeleteConfirmProps {
  group: GroupEntity;
  userId: string;
  onConfirmed: () => void;
  onClose: () => void;
}

function DeleteConfirm({ group, userId, onConfirmed, onClose }: DeleteConfirmProps) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      // 1. Soft-delete: mark entity as inactive
      await updateDoc(doc(db, 'entities', group.id), {
        isActive: false,
        isVisible: false,
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // 2. Remove ALL entityMembers for this entity so it disappears for everyone
      const allMembersSnap = await getDocs(
        query(collection(db, 'entityMembers'), where('entityId', '==', group.id))
      );
      await Promise.all(allMembersSnap.docs.map((m) => deleteDoc(m.ref)));

      // 3. Also deactivate in networkClubs if applicable
      if (group.type === 'networkclub' || group.type === 'community') {
        const ncSnap = await getDocs(
          query(collection(db, 'networkClubs'), where('entityId', '==', group.id))
        );
        if (!ncSnap.empty) {
          await updateDoc(ncSnap.docs[0].ref, {
            isActive: false,
            moderationStatus: 'rejected',
            updatedAt: serverTimestamp(),
          });
        }
      }

      onConfirmed();
    } catch (err) {
      console.error('Error deleting group:', err);
      setError('Verwijderen mislukt. Probeer het opnieuw.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-5 border border-[#E9E7E2]">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: '#F5ECEA' }}>
            <WarningIcon size={20} weight="fill" style={{ color: '#9B6060' }} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-[#3C4F49]">Groep verwijderen?</h3>
            <p className="text-sm text-[#6B7A75] mt-1">
              <span className="font-medium text-[#3C4F49]">{group.name}</span> wordt gedeactiveerd.
              Alle leden verliezen toegang. Dit is niet volledig ongedaan te maken.
            </p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-[#FDF5F5] border border-[#E8CECE] rounded-lg text-sm text-[#8B5050]">
            <AlertCircle size={15} className="shrink-0" />
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm text-[#4F6660] border border-[#D8E4E0] rounded-lg hover:bg-[#F4F6F5] transition-colors"
          >
            Annuleren
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
            style={{ backgroundColor: '#9B6060' }}
            onMouseEnter={e => { if (!deleting) e.currentTarget.style.backgroundColor = '#8A5050'; }}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#9B6060')}
          >
            {deleting ? <><Loader size={14} className="animate-spin" /> Verwijderen...</> : 'Verwijderen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leave Confirm  (for members who want to leave a group they don't own)
// ---------------------------------------------------------------------------

interface LeaveConfirmProps {
  group: GroupEntity;
  userId: string;
  onConfirmed: () => void;
  onClose: () => void;
}

function LeaveConfirm({ group, userId, onConfirmed, onClose }: LeaveConfirmProps) {
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLeave = async () => {
    setLeaving(true);
    setError(null);
    try {
      // Remove the entityMembers document for this user in this entity
      await deleteDoc(doc(db, 'entityMembers', `${group.id}_${userId}`));
      onConfirmed();
    } catch (err) {
      console.error('Error leaving group:', err);
      setError('Verlaten mislukt. Probeer het opnieuw.');
    } finally {
      setLeaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-5 border border-[#E9E7E2]">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: '#F4F6F5' }}>
            <WarningIcon size={20} weight="fill" style={{ color: '#7A9488' }} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-[#3C4F49]">Groep verlaten?</h3>
            <p className="text-sm text-[#6B7A75] mt-1">
              Je verlaat <span className="font-medium text-[#3C4F49]">{group.name}</span>.
              Je kunt alleen terugkomen als de eigenaar je opnieuw uitnodigt.
            </p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-[#FDF5F5] border border-[#E8CECE] rounded-lg text-sm text-[#8B5050]">
            <AlertCircle size={15} className="shrink-0" />
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm text-[#4F6660] border border-[#D8E4E0] rounded-lg hover:bg-[#F4F6F5] transition-colors"
          >
            Annuleren
          </button>
          <button
            onClick={handleLeave}
            disabled={leaving}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
            style={{ backgroundColor: '#8DA8A0' }}
            onMouseEnter={e => { if (!leaving) e.currentTarget.style.backgroundColor = '#7A9488'; }}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#8DA8A0')}
          >
            {leaving ? <><Loader size={14} className="animate-spin" /> Verlaten...</> : 'Groep verlaten'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Group Card
// ---------------------------------------------------------------------------

interface GroupCardProps {
  group: GroupEntity;
  onEdit: () => void;
  onDelete: () => void;
  onLeave: () => void;
}

function GroupCard({ group, onEdit, onDelete, onLeave }: GroupCardProps) {
  const cfg = TYPE_CONFIG[group.type];
  const isOwnerOrOrganizer = group.role === 'owner' || group.role === 'organizer';
  const isMemberOnly = group.role === 'member';

  const placeholderGradients: Record<EntityType, string> = {
    mastermind: 'linear-gradient(135deg, #EDE9F4 0%, #C8BEE0 100%)',
    networkclub: 'linear-gradient(135deg, #DDF0EA 0%, #B8D4CE 100%)',
    community:   'linear-gradient(135deg, #DDEEDD 0%, #BAD2BC 100%)',
  };

  return (
    <div className={`rounded-xl border overflow-hidden transition-all ${cfg.borderClass}`}>
      {/* Cover image / placeholder header */}
      <div className="relative w-full" style={{ height: 90 }}>
        {group.coverImageUrl ? (
          <img
            src={group.coverImageUrl}
            alt=""
            className="w-full h-full object-cover"
            style={{ objectPosition: `${group.coverPosition?.x ?? 50}% ${group.coverPosition?.y ?? 50}%` }}
          />
        ) : (
          <div
            className="w-full h-full"
            style={{ background: placeholderGradients[group.type] }}
          />
        )}
        {/* Type icon overlaid bottom-left */}
        <div
          className="absolute bottom-0 left-4 translate-y-1/2 w-10 h-10 rounded-lg shadow flex items-center justify-center border-2 border-white"
          style={{ backgroundColor: cfg.iconBg }}
        >
          <cfg.Icon size={18} weight="regular" style={{ color: cfg.iconColor }} />
        </div>
        {/* Action buttons top-right */}
        <div className="absolute top-2 right-2 flex items-center gap-1">
          {isOwnerOrOrganizer && (
            <>
              <button
                onClick={onEdit}
                className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/80 backdrop-blur-sm hover:bg-white shadow-sm transition-all"
                title="Bewerken"
              >
                <EditIcon size={13} weight="regular" className="text-[#4F6660]" />
              </button>
              <button
                onClick={onDelete}
                className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/80 backdrop-blur-sm hover:bg-white shadow-sm transition-all"
                title="Groep verwijderen"
              >
                <DeleteIcon size={13} weight="regular" className="text-[#9B6060]" />
              </button>
            </>
          )}
          {isMemberOnly && (
            <button
              onClick={onLeave}
              className="px-2.5 py-1 rounded-lg text-xs font-medium bg-white/80 backdrop-blur-sm border border-white/60 text-[#6B7A75] hover:bg-white hover:text-[#9B6060] transition-all shadow-sm"
            >
              Verlaten
            </button>
          )}
        </div>
      </div>

      {/* Card body */}
      <div className={`px-4 pt-7 pb-4 ${cfg.bgClass}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-[#3C4F49] leading-tight">{group.name}</h3>
              <RoleBadge role={group.role} />
              {group.isActive === false && (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border bg-[#F5ECEA] text-[#9B6060] border-[#E8CECE]">
                  Gedeactiveerd
                </span>
              )}
            </div>
            <p className={`text-xs mt-0.5 ${cfg.colorClass}`}>{cfg.label}</p>
          </div>
        </div>

        {group.description && (
          <p className="text-xs text-[#6B7A75] mt-2 line-clamp-2 leading-relaxed">{group.description}</p>
        )}

        <div className="flex items-center gap-3 mt-3">
          <span className="flex items-center gap-1 text-xs text-[#8A9490]">
            <Users size={11} />
            {group.memberCount ?? 1} {(group.memberCount ?? 1) === 1 ? 'lid' : 'leden'}
          </span>
          <span className="text-xs text-[#9AA49F]">
            {group.visibility === 'public' ? '🌐 Openbaar' : '🔒 Besloten'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const GroupsManager: React.FC<GroupsManagerProps> = ({ onClose }) => {
  const [user] = useAuthState(auth);
  const [groups, setGroups] = useState<GroupEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState<GroupEntity | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<GroupEntity | null>(null);
  const [leavingGroup, setLeavingGroup] = useState<GroupEntity | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await loadUserGroups(user.uid);
      setGroups(data);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3500);
  };

  const ownedGroups = groups.filter((g) => g.role === 'owner' || g.role === 'organizer');
  const memberGroups = groups.filter((g) => g.role === 'member');

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm"
            style={{ background: 'linear-gradient(135deg, #9FB8AE 0%, #7A9488 100%)' }}
          >
            <CommunityIcon size={24} weight="regular" className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#3C4F49]">Mijn Groepen</h1>
            <p className="text-sm text-[#7A9488]">Beheer jouw Mastermind, Netwerkclub of Community</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-[#F4F4F1] flex items-center justify-center transition-colors"
          >
            <CloseIcon size={16} weight="regular" className="text-[#8A9490]" />
          </button>
        )}
      </div>

      {/* Success toast */}
      {successMessage && (
        <div className="flex items-center gap-2 p-3 bg-[#EEF5F2] border border-[#C4D8D4] rounded-xl text-sm text-[#4F6660] font-medium">
          <SuccessIcon size={16} weight="fill" className="text-[#7A9488] shrink-0" />
          {successMessage}
        </div>
      )}

      {/* Create form or "new group" button */}
      {showCreateForm ? (
        <div className="bg-white rounded-xl border border-[#E0EBE8] shadow-sm p-5">
          <CreateForm
            userId={user.uid}
            userName={user.displayName || user.email || 'Gebruiker'}
            userEmail={user.email || ''}
            onCreated={(entityId, type) => {
              localStorage.setItem('activeEntityId', entityId);
              const hubType = type === 'networkclub' ? 'networkclub-hub' : 'mastermind-hub';
              window.location.hash = hubType;
            }}
            onCancel={() => setShowCreateForm(false)}
          />
        </div>
      ) : (
        <button
          onClick={() => setShowCreateForm(true)}
          className="w-full flex items-center justify-center gap-2 p-3.5 rounded-xl border-2 border-dashed border-[#B8D0CA] text-[#4F6660] hover:bg-[#F2F7F5] hover:border-[#9FB8AE] transition-all text-sm font-medium"
        >
          <AddIcon size={16} weight="regular" />
          Nieuwe groep aanmaken
        </button>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader size={24} className="animate-spin text-[#9FB8AE]" />
        </div>
      )}

      {/* Owned/organized groups */}
      {!loading && ownedGroups.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-xs font-semibold text-[#8A9490] uppercase tracking-wider">
            Jouw groepen ({ownedGroups.length})
          </h2>
          {ownedGroups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              onEdit={() => setEditingGroup(group)}
              onDelete={() => setDeletingGroup(group)}
              onLeave={() => {}}
            />
          ))}
        </div>
      )}

      {/* Member groups */}
      {!loading && memberGroups.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-xs font-semibold text-[#8A9490] uppercase tracking-wider">
            Lid van ({memberGroups.length})
          </h2>
          <p className="text-xs text-[#9AA49F] -mt-1">
            Klik op "Verlaten" om je uit een groep te verwijderen.
          </p>
          {memberGroups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              onEdit={() => {}}
              onDelete={() => {}}
              onLeave={() => setLeavingGroup(group)}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && groups.length === 0 && !showCreateForm && (
        <div className="text-center py-12 bg-white rounded-xl border border-[#E9E7E2]">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#EEF5F2' }}>
            <CommunityIcon size={28} weight="regular" style={{ color: '#9FB8AE' }} />
          </div>
          <h3 className="text-base font-semibold text-[#3C4F49] mb-1">Nog geen groepen</h3>
          <p className="text-sm text-[#8A9490] mb-5 max-w-xs mx-auto">
            Maak je eerste Mastermind, Netwerkclub of Community aan om te beginnen.
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-medium rounded-lg transition-colors"
            style={{ backgroundColor: '#8DA8A0' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#7A9488')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#8DA8A0')}
          >
            <AddIcon size={16} weight="regular" />
            Groep aanmaken
          </button>
        </div>
      )}

      {/* Modals */}
      {editingGroup && (
        <EditModal
          group={editingGroup}
          onSaved={() => {
            setEditingGroup(null);
            refresh();
            showSuccess('Groep bijgewerkt.');
          }}
          onClose={() => setEditingGroup(null)}
        />
      )}

      {deletingGroup && user && (
        <DeleteConfirm
          group={deletingGroup}
          userId={user.uid}
          onConfirmed={() => {
            setDeletingGroup(null);
            refresh();
            showSuccess('Groep verwijderd.');
          }}
          onClose={() => setDeletingGroup(null)}
        />
      )}

      {leavingGroup && user && (
        <LeaveConfirm
          group={leavingGroup}
          userId={user.uid}
          onConfirmed={() => {
            setLeavingGroup(null);
            refresh();
            showSuccess(`Je hebt "${leavingGroup.name}" verlaten.`);
          }}
          onClose={() => setLeavingGroup(null)}
        />
      )}
    </div>
  );
};

export default GroupsManager;
